import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';

const UnidadeMedidaModal = ({ isOpen, unidadeMedida, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: null,
    descricao: '',
    descricaoDetalhada: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem('token');

  // Atualiza formData ao abrir modal ou mudar unidade de medida
  useEffect(() => {
    if (isOpen) {
      if (unidadeMedida) {
        setFormData({
          id: unidadeMedida.id,
          descricao: (unidadeMedida.descricao || '').toUpperCase(),
          descricaoDetalhada: (
            unidadeMedida.descricaoDetalhada || ''
          ).toUpperCase(),
        });
        setIsEditing(true);
      } else {
        // Limpa completamente o formulário para nova unidade de medida
        setFormData({
          id: null,
          descricao: '',
          descricaoDetalhada: '',
        });
        setIsEditing(false);
      }
      setError(null);
    }
  }, [isOpen, unidadeMedida]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value.toUpperCase(),
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.descricao.trim()) {
      setError('Descrição é obrigatória');
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const endpoint = isEditing
        ? `${API_BASE_URL.UNIDADE_MEDIDA}/update`
        : `${API_BASE_URL.UNIDADE_MEDIDA}/save`;

      const method = isEditing ? 'PUT' : 'POST';

      const dataToSend = {
        ...formData,
        descricao: formData.descricao.trim().toUpperCase(),
        descricaoDetalhada:
          formData.descricaoDetalhada.trim().toUpperCase() || null,
      };

      await axios({
        method,
        url: endpoint,
        data: dataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      onSuccess();
    } catch (error) {
      console.error('Erro ao salvar unidade de medida:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError(
          isEditing
            ? 'Erro ao atualizar unidade de medida'
            : 'Erro ao criar unidade de medida'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal show={isOpen} onHide={handleClose} backdrop="static">
      <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
        <h6 className="modal-title text-center m-0">
          <i className="bi bi-rulers me-2"></i>
          {isEditing ? 'Editar Unidade de Medida' : 'Nova Unidade de Medida'}
        </h6>
        <button
          className="btn btn-link text-white p-0 mb-0"
          onClick={handleClose}
          title="Fechar"
          style={{ fontSize: '0.9rem', textDecoration: 'none' }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Sigla <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.descricao}
                  onChange={e => handleInputChange('descricao', e.target.value)}
                  placeholder="Ex: UN, M, KG, etc."
                  maxLength={50}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.descricaoDetalhada}
                  onChange={e =>
                    handleInputChange('descricaoDetalhada', e.target.value)
                  }
                  placeholder="Descrição mais detalhada da unidade de medida (opcional)"
                  maxLength={255}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant={isEditing ? 'primary' : 'success'}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {isEditing ? 'Atualizando...' : 'Salvando...'}
              </>
            ) : (
              <>
                <i
                  className={`bi ${
                    isEditing ? 'bi-check-lg' : 'bi-plus-lg'
                  } me-1`}
                ></i>
                {isEditing ? 'Atualizar' : 'Salvar'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

UnidadeMedidaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  unidadeMedida: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default UnidadeMedidaModal;
